<template>
  <div class="search-view">
    <hero-small>
      <h1 class="text-white bold m-0 text-uppercase">
        {{$t('common.search')}}
      </h1>
    </hero-small>
    <div v-if="searchPhrase" class="container pt-4" id="results">
      <div class="row">
        <div class="col-6 col-sm-8 col-lg-9">
          <div class="results-breadcrumbs pb-2 mb-4">
            <p class="text-uppercase text-secondary m-0">
              {{$t('stepsView.resultsFor')}}
            </p>
            <div class="text-primary text-uppercase">
              {{$t('common.search')}} > <span class="bold">{{searchPhrase}}</span>
            </div>
          </div>
        </div>
        <div class="col-6 col-sm-4 col-lg-3">
          <button v-if="!resultsLoading"
                  @click="copyUrlToClipboard"
                  class="btn btn-secondary w-100 text-xs mt-1 mt-lg-0 mt-xl-2">{{$t('search.copyUrlToClipboard')}}</button>
        </div>
      </div>
      <div v-if="!resultsLoading && searchCompleted" class="row">
        <div class="col-12 col-lg-9">
          <div class="mb-5">
            <b-alert show variant="primary">
              <div class="d-flex align-items-center part-number-info-message">
                <p class="text-xs m-0">

                  <span v-if="searchedByIncompleteProductCode && productGroupFromProductCode"
                        class="d-flex">
                    <img src="@/assets/icons/warning-1.svg" class="mr-3">
                    <span v-html="$t('search.incorrectProductCodeMessage', {productGroup: productGroupFromProductCode.name})"></span>
                  </span>

                  <span v-else-if="!searchedByIncompleteProductCode && productGroupFromProductCode">
                    {{$t('search.resultsForProductGroupMessage')}} <span class="bold">{{productGroupFromProductCode.name}}</span>
                  </span>

                  <span v-else-if="!results.length && !relatedMaterials.length">
                    {{$t('search.noProductGroupMessage')}}
                  </span>

                  <span v-else-if="results.length">
                    {{$t('search.resultsForTextPhraseMessage')}} <span class="bold">{{searchPhrase}}</span>
                  </span>
                </p>
              </div>
            </b-alert>
            <b-alert v-if="validPartNumber" show variant="primary">
              <p class="text-xs mb-0">
                {{$t('search.checkProductIn')}}&nbsp;
                <a :href="`http://customerportal.nissens.com/Product/SimpleProduct?nissensNo=${validPartNumber}`"
                   target="_blank">
                  <span class="bold">{{$t('search.nissensCatalogue')}}</span>
                </a>
              </p>
            </b-alert>
          </div>
          <div v-if="isHotlineTriggerPhrase(searchPhrase) || isHotlineTriggerProductGroup" class="mb-5">
            <HotlineNumber :product="productGroupFromProductCode ? productGroupFromProductCode.name : searchPhrase"/>
          </div>
        </div>
      </div>
      <div v-if="resultsLoading" class="row">
        <div class="col-12 col-lg-9">
          <div class="text-center py-5">
            <b-spinner type="grow" variant="secondary"></b-spinner>
          </div>
        </div>
      </div>
      <div v-else id="direct-results" class="mb-4">
        <div class="row">
          <div class="col-12 col-lg-9">
            <div v-b-toggle.direct-search
                class="search-collapse border-top border-secondary px-3">
              <h2 class="my-3 text-md">{{$t('search.directSearchResults')}}</h2>
              <span class="ml-3">
                <img src="@/assets/icons/plus-secondary-thin.svg" class="icon-plus">
                <img src="@/assets/icons/arrow-secondary.svg" class="icon-arrow">
              </span>
            </div>
          </div>
        </div>
        <b-collapse id="direct-search" visible>
          <div class="row pt-4 pb-3 position-relative">
            <transition name="fade">
              <div v-if="resultsFiltering" class="filtering-overlay"></div>
            </transition>
            <div class="col-12 col-lg-9">
              <div v-if="results.length">
                <div v-for="(result, i) in results" :key="`search-result-${i}`" class="mb-4">
                  <SearchItem :material="result" />
                </div>
              </div>
              <div v-else class="mb-4 mb-lg-0">
                <NoContent :msg="$t('common.noMaterials')" />
              </div>
            </div>
            <div class="col-12 col-lg-3 mb-4">
              <SidebarSearchFilters :materialTypes="availableMaterialTypes"
                                    :productCategories="availableProductCategories"
                                    :productGroups="availableProductGroups" />
            </div>
          </div>
        </b-collapse>
      </div>
      <div class="row">
        <div class="col-12 col-lg-9">
          <div v-if="relatedMaterialsLoading" class="text-center">
            <b-spinner type="grow" variant="secondary"></b-spinner>
          </div>
        </div>
      </div>
      <div v-if="relatedMaterials.length" id="related-materials">
        <div class="row">
          <div class="col-12 col-lg-9">
            <div v-b-toggle.related-search
                class="search-collapse border-top border-secondary px-3">
              <h2 class="my-3 text-md">
                {{$t('search.dbSearchResults')}},
                <span v-if="productGroupFromProductCode.name && relatedMaterialsTotalItems" class="bold">
                  {{productGroupFromProductCode.name}}:&nbsp;{{relatedMaterialsTotalItems}}
                </span>
              </h2>
              <span class="ml-3">
                <img src="@/assets/icons/plus-secondary-thin.svg" class="icon-plus">
                <img src="@/assets/icons/arrow-secondary.svg" class="icon-arrow">
              </span>
            </div>
          </div>
        </div>
        <b-collapse id="related-search" visible>
          <div class="row pt-4 pb-3 position-relative">
            <transition name="fade">
              <div v-if="relatedMaterialsFiltering" class="filtering-overlay"></div>
            </transition>
            <div class="col-12 col-lg-9">
              <div v-for="(material, i) in relatedMaterials" :key="`related-result-${i}`" class="mb-4">
                <SearchItem :material="material" />
              </div>
              <div id="related-materials-loading-more"
                    class="py-1">
                <div v-if="relatedMaterialsLoadingMore">
                  <div class="mb-4">
                    <SearchItemSkeleton />
                  </div>
                  <div class="mb-4">
                    <SearchItemSkeleton />
                  </div>
                </div>
              </div>
              <div v-if="!relatedMaterialsReachedTheEnd && !relatedMaterialsLoadingMore" class="text-center">
                <button class="btn btn-secondary btn-rounded text-uppercase py-2 px-4 mb-5 mb-lg-0"
                        @click="searchForRelatedArticles(productGroupFromProductCode.id, true)">
                    {{$t('common.loadMore')}}
                </button>
              </div>
            </div>
            <div class="col-12 col-lg-3 mb-4">
              <div v-if="relatedMaterialsMaterialTypes" class="sticky">
                <SidebarFilterMaterialType :materialTypes="relatedMaterialsMaterialTypes"
                                           :loading="relatedMaterialsFiltering"/>
              </div>
            </div>
          </div>
        </b-collapse>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import HeroSmall from '@/components/HeroSmall.vue';
import SearchItem from '@/components/SearchItem.vue';
import SearchItemSkeleton from '@/components/SearchItemSkeleton.vue';
import NoContent from '@/components/NoContent.vue';
import SidebarSearchFilters from '@/components/SidebarSearchFilters.vue';
import SidebarFilterMaterialType from '@/components/SidebarFilterMaterialType.vue';
import HotlineNumber from '@/components/HotlineNumber.vue';

export default {
  components: {
    HeroSmall,
    SearchItem,
    SearchItemSkeleton,
    NoContent,
    SidebarSearchFilters,
    SidebarFilterMaterialType,
    HotlineNumber,
  },
  metaInfo() {
    return {
      title: `${this.$t('common.search')} - Nissens Support`,
    };
  },
  data() {
    return {
      searchPhrase: '',
      resultsLoading: true,
      resultsFiltering: false,
      results: Array,
      availableMaterialTypes: null,
      availableProductCategories: null,
      availableProductGroups: null,
      selectedMaterialType: null,
      selectedProductCategories: [],
      selectedProductGroups: [],
      productGroupFromProductCode: null,
      isDefaultSearch: false,
      // related materials
      relatedMaterialsLoading: false,
      relatedMaterialsFiltering: false,
      relatedMaterialsCurrentPage: 1,
      relatedMaterialsLoadingMore: false,
      relatedMaterialsTotalPages: null,
      relatedMaterialsReachedTheEnd: false,
      relatedMaterials: [],
      relatedMaterialsMaterialTypes: null,
      relatedMaterialsTotalItems: null,
      selectedRelatedMaterialType: null,
      searchedByIncompleteProductCode: false,
      searchCompleted: false,
      isHotlineTriggerProductGroup: false,
      validPartNumber: '',
    };
  },
  mounted() {
    if (this.$route.params.query) {
      this.$searchQuery = this.$route.params.query;
    }
    this.searchPhrase = this.$route.params.query ? this.$route.params.query : this.$searchQuery;
    // Material Type
    this.$root.$on('materialTypeSelected', (type) => {
      this.selectedMaterialType = type.term_id;
      this.search(true);
    });
    this.$root.$on('materialTypeDeselected', () => {
      this.selectedMaterialType = null;
      this.search(true);
    });
    // Product Category
    this.$root.$on('productCategorySelected', (category) => {
      this.selectedProductCategories.push(category.term_id);
      this.search(true);
    });
    this.$root.$on('productCategoryDeselected', (category) => {
      const index = this.selectedProductCategories.indexOf(category.term_id);
      this.selectedProductCategories.splice(index, 1);
      this.search(true);
    });
    // Product Group
    this.$root.$on('productGroupSelected', (group) => {
      this.selectedProductGroups.push(group.term_id);
      this.search(true);
    });
    this.$root.$on('productGroupDeselected', (group) => {
      const index = this.selectedProductGroups.indexOf(group.term_id);
      this.selectedProductGroups.splice(index, 1);
      this.search(true);
    });
    // Related materials - material types selection
    this.$root.$on('relatedMaterialTypeSelected', (type) => {
      this.selectedRelatedMaterialType = type.id;
      this.searchForRelatedArticles(this.productGroupFromProductCode.id, false, true);
    });
    this.$root.$on('relatedMaterialTypeDeselected', () => {
      this.selectedRelatedMaterialType = null;
      this.searchForRelatedArticles(this.productGroupFromProductCode.id, false, true);
    });
  },
  watch: {
    // eslint-disable-next-line
    searchPhrase: _.debounce(function (val) {
      if (val.length > 1) {
        if (this.isMobile()) {
          this.$scrollTo('#results', 600, { offset: -30 });
        }
        this.search(false, true);
      }
    }, 700),
    // eslint-disable-next-line
    $searchQuery: function (val) {
      this.searchPhrase = val;
      this.selectedMaterialType = null;
      this.selectedProductCategories = [];
      this.selectedProductGroups = [];
      this.relatedMaterials = [];
      this.relatedMaterialsMaterialTypes = null;
      this.validPartNumber = '';
      if (val.length <= 1) {
        this.searchPhrase = '';
        this.resultsLoading = false;
      } else {
        this.resultsLoading = true;
      }
    },
    // eslint-disable-next-line
    resultsLoading: function (val) {
      this.$root.$emit('searchResultsLoading', val);
    },
  },
  destroyed() {
    this.$searchQuery = '';
  },
  methods: {
    search(onFiltering = false, onTyping = false) {
      let params = '';
      let materialTypesToExclude = [];
      let categoriesToExclude = [];
      let groupsToExclude = [];
      const selectedCategories = this.selectedProductCategories.concat(this.selectedProductGroups);
      if (this.selectedMaterialType) {
        selectedCategories.push(this.selectedMaterialType);
        materialTypesToExclude = this.availableMaterialTypes.filter(
          (v) => v.term_id !== this.selectedMaterialType,
        ).map((v) => v.term_id);
      }
      if (selectedCategories.length) {
        params += `&material_categories=${selectedCategories.join()}`;
      }
      if (this.selectedProductCategories.length) {
        categoriesToExclude = this.availableProductCategories.filter(
          (v) => !v.selected,
        ).map((v) => v.term_id);
      }
      if (this.selectedProductGroups.length) {
        groupsToExclude = this.availableProductGroups.filter(
          (v) => !v.selected,
        ).map((v) => v.term_id);
      }
      const exclude = categoriesToExclude.concat(groupsToExclude).concat(materialTypesToExclude);
      if (exclude.length) {
        params += `&material_categories_exclude=${exclude}`;
      }
      // TODO: implement proper pagination!
      params += '&page=1&per_page=50';
      if (onTyping) {
        this.productGroupFromProductCode = null;
        this.relatedMaterialsLoading = false;
        this.relatedMaterialsCurrentPage = 1;
        this.relatedMaterialsLoadingMore = false;
        this.relatedMaterialsTotalPages = null;
        this.relatedMaterialsReachedTheEnd = false;
        this.searchedByIncompleteProductCode = false;
        this.resultsLoading = true;
        this.isDefaultSearch = false;
        this.isHotlineTriggerProductGroup = false;
      }
      if (onFiltering) {
        if (this.isMobile()) {
          this.$scrollTo('#direct-results', 600, { offset: -100 });
        }
        this.resultsFiltering = true;
      } else {
        this.searchCompleted = false;
      }
      this.resultsFiltering = onFiltering;

      this.updateUrlBasedOnSearchQuery();

      this.$http.get(`${process.env.VUE_APP_API_URL}/materials?search=${this.searchPhrase}&lang=${this.$i18n.locale}${params}`)
        .then((response) => {
          const results = response.data;
          if (onTyping) {
            if (!this.selectedMaterialType) {
              this.availableMaterialTypes = this.getArrayUniqueValuesAndCountDuplicates(
                results.map((item) => {
                  if (!Array.isArray(item.categories) && item.categories.material_type) {
                    return item.categories.material_type;
                  }
                  return false;
                }),
              ).filter((v) => 'term_id' in v);
            }
            if (!this.selectedProductCategories.length) {
              this.availableProductCategories = this.getArrayUniqueValuesAndCountDuplicates(
                results.map((item) => {
                  if (!Array.isArray(item.categories) && item.categories.product_category) {
                    return item.categories.product_category;
                  }
                  return false;
                }),
              ).filter((v) => 'term_id' in v);
            }
            if (!this.selectedProductGroups.length) {
              this.availableProductGroups = this.getArrayUniqueValuesAndCountDuplicates(
                results.map((item) => {
                  if (!Array.isArray(item.categories) && item.categories.product_group) {
                    return item.categories.product_group;
                  }
                  return false;
                }),
              ).filter((v) => 'term_id' in v);
            }
            this.resultsLoading = false;
            if (this.isQueryHasPartNumber(this.searchPhrase)) {
              this.getRelatedMaterialsBasedOnPartNumber(this.searchPhrase);
            } else if (this.isQueryStartWithTwoNumbers(this.searchPhrase)) {
              this.searchForProductGroupBasedOnFirstTwoCharacters(this.searchPhrase);
            } else {
              this.isDefaultSearch = true;
              this.searchCompleted = true;
            }
          }
          if (onFiltering) {
            this.resultsFiltering = false;
          }
          this.results = results;
        });
    },
    getArrayUniqueValuesAndCountDuplicates(array) {
      return [...array.reduce((mp, o) => {
        if (!mp.has(o.term_id)) mp.set(o.term_id, { ...o, quantity: 0 });
        const res = mp;
        res.get(o.term_id).quantity += 1;
        return res;
      }, new Map()).values()];
    },
    isQueryHasPartNumber(query) {
      // Check if query is a part number.
      // Rules:
      // * 5 to 7 digits
      // * can contain A letter at the end
      return /^[0-9]{5,7}[Aa]?$/.test(query);
    },
    isQueryStartWithTwoNumbers(query) {
      return /^[0-9]{2}/.test(query);
    },
    async getRelatedMaterialsBasedOnPartNumber(partNumber) {
      const partNumberDetails = await this.$http.get(
        `${process.env.VUE_APP_API_SUPPORT_URL}/part_number/${partNumber.toUpperCase()}`,
      );
      if (partNumberDetails.data.product_group_name === 'Turbo') {
        this.isHotlineTriggerProductGroup = true;
      }
      // Valid part number in DB
      if (partNumberDetails.data.part_number) {
        this.validPartNumber = partNumberDetails.data.part_number;
      }
      if (partNumberDetails.data.product_group_id) {
        // If lang is not EN, get related translated product group
        const productGroup = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/material_categories/${partNumberDetails.data.product_group_id}`,
        );
        if (this.$i18n.locale !== 'en') {
          if (productGroup.data.translations[this.$i18n.locale]) {
            const translatedProductGroup = await this.$http.get(
              `${process.env.VUE_APP_API_URL}/material_categories/${productGroup.data.translations[this.$i18n.locale]}`,
            );
            this.productGroupFromProductCode = translatedProductGroup.data;
            this.getMaterialTypesBasedOnProductGroupId(this.productGroupFromProductCode.id);
            this.searchForRelatedArticles(this.productGroupFromProductCode.id);
          }
        } else {
          this.productGroupFromProductCode = productGroup.data;
          this.getMaterialTypesBasedOnProductGroupId(this.productGroupFromProductCode.id);
          this.searchForRelatedArticles(this.productGroupFromProductCode.id);
        }
        this.searchCompleted = true;
      } else {
        this.searchForProductGroupBasedOnFirstTwoCharacters(partNumber);
      }
    },
    async searchForProductGroupBasedOnFirstTwoCharacters(query) {
      const number = query.substring(0, 2);
      const searchedProductGroup = await this.$http.get(
        `${process.env.VUE_APP_API_SUPPORT_URL}/product_groups/${number}`,
      );
      if (searchedProductGroup.data[0]?.product_group_name === 'Turbo') {
        this.isHotlineTriggerProductGroup = true;
      }
      if (searchedProductGroup.data.length === 1) {
        const productGroup = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/material_categories/${searchedProductGroup.data[0].product_group_id}`,
        );
        if (this.$i18n.locale !== 'en') {
          const translatedProductGroup = await this.$http.get(
            `${process.env.VUE_APP_API_URL}/material_categories/${productGroup.data.translations[this.$i18n.locale]}`,
          );
          this.productGroupFromProductCode = translatedProductGroup.data;
          await this.getMaterialTypesBasedOnProductGroupId(this.productGroupFromProductCode.id);
          this.searchForRelatedArticles(this.productGroupFromProductCode.id);
        } else {
          this.productGroupFromProductCode = productGroup.data;
          await this.getMaterialTypesBasedOnProductGroupId(this.productGroupFromProductCode.id);
          this.searchForRelatedArticles(this.productGroupFromProductCode.id);
        }
        this.searchedByIncompleteProductCode = true;
      } else {
        this.searchCompleted = true;
      }
    },
    async getMaterialTypesBasedOnProductGroupId(productGroupId) {
      const params = {
        parent: productGroupId,
        hide_empty: true,
      };
      const materialTypes = await this.$http.get(
        `${process.env.VUE_APP_API_URL}/material_categories`, { params },
      );
      if (materialTypes) {
        this.relatedMaterialsMaterialTypes = materialTypes.data;
      }
    },
    searchForRelatedArticles(productGroupId, loadingMore = false, isFiltering = false) {
      if (!isFiltering && !loadingMore) {
        this.relatedMaterialsTotalItems = null;
        this.selectedRelatedMaterialType = null;
      }
      if (isFiltering) {
        this.relatedMaterialsCurrentPage = 1;
        this.relatedMaterialsFiltering = true;
      } else if (loadingMore) {
        this.relatedMaterialsCurrentPage += 1;
        this.relatedMaterialsLoadingMore = true;
        this.$scrollTo('#related-materials-loading-more', 600, { offset: -50 });
      } else {
        this.relatedMaterialsLoading = true;
      }
      const paginationParams = {
        page: this.relatedMaterialsCurrentPage,
        per_page: 100,
      };
      const params = {
        lang: this.$i18n.locale,
        material_categories: productGroupId,
        ...paginationParams,
      };
      // prevent to display duplicates
      // if (this.results.length) {
      //   const materialsToExclude = [];
      //   this.results.forEach((element) => {
      //     materialsToExclude.push(element.id);
      //   });
      //   params.exclude = materialsToExclude.join();
      // }
      if (this.selectedRelatedMaterialType) {
        params.material_categories = this.selectedRelatedMaterialType;
      }
      this.$http.get(`${process.env.VUE_APP_API_URL}/materials`, { params })
        .then((response) => {
          if (response) {
            if (loadingMore) {
              this.relatedMaterials = this.relatedMaterials.concat(response.data);
              this.relatedMaterialsLoadingMore = false;
            } else {
              this.relatedMaterials = this.sortRelatedMaterialsByCategoriesOrder(response.data);
            }
            if (!isFiltering && !loadingMore) {
              this.relatedMaterialsTotalItems = response.headers['x-wp-total'];
            }
            this.relatedMaterialsTotalPages = Number(response.headers['x-wp-totalpages']);
          }
          if (isFiltering) {
            if (this.isMobile()) {
              this.$scrollTo('#related-materials', 600, { offset: -100 });
            }
            this.relatedMaterialsFiltering = false;
          } else {
            this.relatedMaterialsLoading = false;
          }
          this.searchCompleted = true;
          this.relatedMaterialsReachedTheEnd = this.relatedMaterialsCurrentPage === this.relatedMaterialsTotalPages;
        }).catch(() => {
          this.relatedMaterialsLoading = false;
          this.searchCompleted = true;
        });
    },
    sortRelatedMaterialsByCategoriesOrder(materials) {
      if (materials && this.relatedMaterialsMaterialTypes) {
        const sortedMaterials = [];
        this.relatedMaterialsMaterialTypes.forEach((category) => {
          materials.forEach((material) => {
            if (material.material_categories.includes(category.id)) {
              sortedMaterials.push(material);
            }
          });
        });
        return sortedMaterials;
      }
      return materials;
    },
    copyUrlToClipboard() {
      this.$copyText(window.location.href).then(() => {
        this.$bvToast.toast('Success', {
          title: this.$t('search.urlCopiedToClipboard'),
          variant: 'light',
          solid: true,
          autoHideDelay: 5000,
          toaster: 'b-toaster-bottom-center',
          bodyClass: 'd-none',
          toastClass: 'toast-secondary',
        });
      });
    },
    updateUrlBasedOnSearchQuery() {
      let url = window.location.origin + this.$route.fullPath;
      if (this.$route.params.query) {
        const splittedUrl = url.split('/search/');
        url = `${splittedUrl[0]}/search/${this.searchPhrase}`;
      } else {
        url += `/${this.searchPhrase}`;
      }
      window.history.pushState({}, '', url);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common.scss';
.results-breadcrumbs {
  border-bottom: 1px solid $secondary;
  p {
    font-size: 12px;
  }
  div {
    font-size: 14px;
  }
}
@include media-breakpoint-down(lg) {
  .results-breadcrumbs {
    p {
      font-size: 9px;
    }
    div {
      font-size: 10px;
    }
  }
}
.part-number-info-message {
  img {
    max-width: 40px;
  }
}

.search-collapse {
  display: flex;
  justify-content: space-between;
  align-items: center;
  &.collapsed {
    .icon-arrow {
      display: none;
    }
  }
  &.not-collapsed {
    .icon-plus {
      display: none;
    }
  }
  img {
    max-width: 20px;
    &.icon-arrow {
      transform: rotate(-90deg);
    }
  }
}
.filtering-overlay {
  position: absolute;
  left: 0; right: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255,255,255, .5);
  z-index: 9;
}
</style>
